import type { TInlineProps } from "@/ui/components/layout/Inline";
import { cn } from "@/ui/lib";
import { DefinedDefaults } from "@/ui/lib/types";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const COUNTER_INPUT_ARIA_LABELS = {
  decrement: "Decrease quantity",
  increment: "Increase quantity",
  quantity: "Quantity",
} as const;

export const COUNTER_INPUT_FIELD_CLASS = cn(
  "h-full border border-strong",
  "bg-component-input-focused px-2 py-1 text-center font-bold",
  "[&::-webkit-inner-spin-button]:appearance-none",
  "[&::-webkit-outer-spin-button]:appearance-none",
);

export const COUNTER_INPUT_VARIANT_CLASS_MAP = {
  default: {
    inline: "items-end",
    button: "size-7",
    inputContainer: "w-fit shrink-0",
    input: "min-w-8 w-auto [field-sizing:content]",
  },
  fullWidth: {
    inline: "w-full items-stretch",
    button: "size-9",
    inputContainer: "min-w-0 flex-1",
    input: "min-w-0 w-full [field-sizing:fixed]",
  },
} as const;

/*-------------------------------------
|  Types
-------------------------------------*/

export type TCounterInputVariant = keyof typeof COUNTER_INPUT_VARIANT_CLASS_MAP;

export type TCounterInputProps = {
  label: string;
  hideLabel?: boolean;
  variant?: TCounterInputVariant;
  displayValue?: string | number;
  suffix?: string;
  value: number;
  onChange: (nextValue: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
} & Omit<TInlineProps, "data-component" | "onChange">;

export type TUseCounterInputOptions = Required<Pick<
  TCounterInputProps,
  "value" | "onChange" | "min" | "step" | "disabled"
>> & Pick<TCounterInputProps, "max">;

type TCounterInputDefaultProps = DefinedDefaults<
  TCounterInputProps,
  "min" | "step" | "variant"
>;

/*-------------------------------------
|  Defaults
-------------------------------------*/

export const COUNTER_INPUT_DEFAULTS = {
  min: 0,
  step: 1,
  variant: "default",
} satisfies TCounterInputDefaultProps;
